import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getReferralCode, getReferralStats, getReferralLink } from '../services/referralService';

function timeAgo(dateStr) {
    const now = new Date();
    const date = new Date(dateStr);
    const diffDays = Math.floor((now - date) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: diffDays > 365 ? 'numeric' : undefined });
}

function Referral() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ totalReferrals: 0, totalEarned: 0, referrals: [], currentCommission: 10 });
    const [referralCode, setReferralCode] = useState('');
    const [copied, setCopied] = useState(false);

    const loadData = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        const [codeResult, statsResult] = await Promise.all([
            getReferralCode(user.id),
            getReferralStats(user.id),
        ]);
        setReferralCode(codeResult.referralCode || '');
        if (!statsResult.error && statsResult.stats) {
            setStats(statsResult.stats);
        }
        setLoading(false);
    }, [user]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleCopyLink = () => {
        const link = getReferralLink(referralCode);
        navigator.clipboard.writeText(link).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        }).catch(() => {
            // Fallback
            const el = document.createElement('textarea');
            el.value = link;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        });
    };

    const handleShare = async () => {
        const link = getReferralLink(referralCode);
        const text = `Join Sigma Bank and earn crypto! Use my referral code ${referralCode} when you sign up.`;
        if (navigator.share) {
            try {
                await navigator.share({ title: 'Join Sigma Bank', text, url: link });
            } catch (_) { /* user dismissed */ }
        } else {
            handleCopyLink();
        }
    };

    const s = styles;

    return (
        <div style={s.screen}>
            {/* Header */}
            <div style={s.header}>
                <Link to="/welcome" style={s.backBtn}>
                    <i className="bx bx-chevron-left" style={{ fontSize: 26 }}></i>
                </Link>
                <span style={s.headerTitle}>Referral Program</span>
                <div style={{ width: 36 }} />
            </div>

            <div style={s.content}>
                {/* Hero banner */}
                <div style={s.heroBanner}>
                    <div style={s.heroIconRing}>
                        <i className="bx bxs-gift" style={{ fontSize: 30, color: '#A78BFA' }}></i>
                    </div>
                    <div style={s.heroText}>
                        <p style={s.heroTitle}>Invite &amp; Earn</p>
                        <p style={s.heroSubtitle}>Earn <span style={{ color: '#A78BFA', fontWeight: 700 }}>${(stats.currentCommission ?? 10).toFixed(2)}</span> for every friend you refer</p>
                    </div>
                </div>

                {/* Stats */}
                <div style={s.statsRow}>
                    <div style={s.statCard}>
                        {loading ? (
                            <div style={s.statSkeleton} />
                        ) : (
                            <p style={{ ...s.statValue, color: '#10B981' }}>
                                ${stats.totalEarned.toFixed(2)}
                            </p>
                        )}
                        <p style={s.statLabel}>Total Earned</p>
                    </div>
                    <div style={s.statSep} />
                    <div style={s.statCard}>
                        {loading ? (
                            <div style={s.statSkeleton} />
                        ) : (
                            <p style={s.statValue}>{stats.totalReferrals}</p>
                        )}
                        <p style={s.statLabel}>People Referred</p>
                    </div>
                    <div style={s.statSep} />
                    <div style={s.statCard}>
                        <p style={{ ...s.statValue, color: '#A78BFA' }}>${(stats.currentCommission ?? 10).toFixed(2)}</p>
                        <p style={s.statLabel}>Per Referral</p>
                    </div>
                </div>

                {/* Referral code */}
                <div style={s.codeSection}>
                    <p style={s.sectionLabel}>YOUR REFERRAL LINK</p>
                    <div style={s.codeCard}>
                        <div style={s.codeDisplay}>
                            <i className="bx bx-link" style={{ fontSize: 16, color: 'rgba(255,255,255,0.35)', marginRight: 8 }}></i>
                            <span style={s.codeText}>{referralCode ? `...?ref=${referralCode}` : '—'}</span>
                        </div>
                        <div style={s.codeActions}>
                            <button style={s.copyBtn} onClick={handleCopyLink} disabled={!referralCode}>
                                <i className={`bx ${copied ? 'bx-check' : 'bx-copy'}`} style={{ fontSize: 15 }}></i>
                                {copied ? 'Copied!' : 'Copy'}
                            </button>
                            <button style={s.shareBtn} onClick={handleShare} disabled={!referralCode}>
                                <i className="bx bx-share-alt" style={{ fontSize: 15 }}></i>
                                Share
                            </button>
                        </div>
                    </div>

                    {/* Code badge */}
                    {referralCode && (
                        <div style={s.codeBadgeRow}>
                            <span style={s.codeBadgeLabel}>Code:</span>
                            <span style={s.codeBadge}>{referralCode}</span>
                        </div>
                    )}
                </div>

                {/* How it works */}
                <div style={s.howSection}>
                    <p style={s.sectionLabel}>HOW IT WORKS</p>
                    <div style={s.stepList}>
                        {[
                            { icon: 'bx-share-alt', label: 'Share your link', sub: 'Send your unique referral link to friends' },
                            { icon: 'bx-user-plus', label: 'Friend signs up', sub: 'They create an account and verify their email' },
                            { icon: 'bx-dollar-circle', label: `You earn $${(stats.currentCommission ?? 10).toFixed(2)}`, sub: 'Added to your balance once they verify' },
                        ].map((step, i) => (
                            <div key={i} style={s.stepItem}>
                                <div style={s.stepIconWrap}>
                                    <i className={`bx ${step.icon}`} style={{ fontSize: 18, color: '#A78BFA' }}></i>
                                </div>
                                <div style={s.stepText}>
                                    <p style={s.stepLabel}>{step.label}</p>
                                    <p style={s.stepSub}>{step.sub}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Referred people */}
                <div style={s.peopleSection}>
                    <p style={s.sectionLabel}>
                        PEOPLE YOU'VE REFERRED&nbsp;
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>({stats.totalReferrals})</span>
                    </p>

                    {loading ? (
                        <div style={s.loadingBox}>
                            <div style={s.spinner}></div>
                        </div>
                    ) : stats.referrals.length === 0 ? (
                        <div style={s.emptyState}>
                            <i className="bx bx-group" style={{ fontSize: 44, color: 'rgba(255,255,255,0.15)', display: 'block', marginBottom: 12 }}></i>
                            <p style={s.emptyTitle}>No referrals yet</p>
                            <p style={s.emptySub}>Share your link above to start earning</p>
                        </div>
                    ) : (
                        <div style={s.personList}>
                            {stats.referrals.map((person) => {
                                const name = person.full_name || person.email?.split('@')[0] || 'User';
                                const initial = name.charAt(0).toUpperCase();
                                const colors = ['#0072CE', '#19bfb1', '#7C3AED', '#10B981', '#F59E0B'];
                                const color = colors[name.charCodeAt(0) % colors.length];
                                return (
                                    <div key={person.id} style={s.personItem}>
                                        <div style={{ ...s.personAvatar, background: `linear-gradient(135deg, ${color} 0%, ${color}99 100%)` }}>
                                            {initial}
                                        </div>
                                        <div style={s.personInfo}>
                                            <p style={s.personName}>{name}</p>
                                            <p style={s.personDate}>Joined {timeAgo(person.created_at)}</p>
                                        </div>
                                        <div style={s.personBonus}>
                                            {person.bonusEarned === null ? (
                                                <>
                                                    <p style={s.personBonusPending}>—</p>
                                                    <p style={s.personBonusLabel}>pending</p>
                                                </>
                                            ) : (
                                                <>
                                                    <p style={s.personBonusAmt}>+${person.bonusEarned.toFixed(2)}</p>
                                                    <p style={s.personBonusLabel}>earned</p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Bottom padding for nav bar */}
                <div style={{ height: 90 }} />
            </div>
        </div>
    );
}

const styles = {
    screen: {
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #07092A 0%, #0D1240 100%)',
        color: '#fff',
    },
    header: {
        position: 'sticky',
        top: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 16px',
        background: 'rgba(7, 9, 42, 0.92)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
    },
    backBtn: {
        width: 36,
        height: 36,
        borderRadius: 10,
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.09)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        flexShrink: 0,
    },
    headerTitle: {
        fontSize: 17,
        fontWeight: 700,
        color: '#fff',
        letterSpacing: 0.2,
    },
    content: {
        padding: '20px 16px 0',
    },

    /* Hero */
    heroBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        background: 'linear-gradient(135deg, rgba(124,58,237,0.18) 0%, rgba(13,18,64,0.8) 100%)',
        border: '1px solid rgba(124,58,237,0.22)',
        borderRadius: 20,
        padding: '18px 20px',
        marginBottom: 16,
    },
    heroIconRing: {
        width: 56,
        height: 56,
        borderRadius: 16,
        background: 'rgba(124,58,237,0.2)',
        border: '1px solid rgba(167,139,250,0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    heroText: {
        flex: 1,
    },
    heroTitle: {
        fontSize: 20,
        fontWeight: 800,
        color: '#fff',
        margin: '0 0 4px',
        letterSpacing: 0.3,
    },
    heroSubtitle: {
        fontSize: 13,
        color: 'rgba(255,255,255,0.6)',
        margin: 0,
    },

    /* Stats */
    statsRow: {
        display: 'flex',
        alignItems: 'center',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 18,
        padding: '18px 0',
        marginBottom: 20,
    },
    statCard: {
        flex: 1,
        textAlign: 'center',
        padding: '0 8px',
    },
    statValue: {
        fontSize: 22,
        fontWeight: 800,
        color: '#fff',
        margin: '0 0 4px',
        letterSpacing: 0.5,
    },
    statLabel: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        margin: 0,
    },
    statSkeleton: {
        height: 28,
        borderRadius: 6,
        background: 'rgba(255,255,255,0.08)',
        marginBottom: 4,
        width: '60%',
        margin: '0 auto 4px',
    },
    statSep: {
        width: 1,
        height: 40,
        background: 'rgba(255,255,255,0.08)',
    },

    /* Referral Code */
    codeSection: {
        marginBottom: 20,
    },
    sectionLabel: {
        fontSize: 11,
        fontWeight: 600,
        color: 'rgba(255,255,255,0.35)',
        letterSpacing: 1,
        textTransform: 'uppercase',
        marginBottom: 10,
    },
    codeCard: {
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.09)',
        borderRadius: 16,
        padding: '14px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
    },
    codeDisplay: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        overflow: 'hidden',
    },
    codeText: {
        fontSize: 13,
        color: 'rgba(255,255,255,0.55)',
        fontFamily: 'monospace',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    codeActions: {
        display: 'flex',
        gap: 8,
        flexShrink: 0,
    },
    copyBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        background: 'rgba(25,191,177,0.12)',
        border: '1px solid rgba(25,191,177,0.25)',
        color: '#19bfb1',
        borderRadius: 10,
        padding: '7px 12px',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 0.15s',
    },
    shareBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        background: 'rgba(124,58,237,0.15)',
        border: '1px solid rgba(167,139,250,0.3)',
        color: '#A78BFA',
        borderRadius: 10,
        padding: '7px 12px',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 0.15s',
    },
    codeBadgeRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginTop: 12,
        paddingLeft: 4,
    },
    codeBadgeLabel: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.4)',
    },
    codeBadge: {
        background: 'rgba(124,58,237,0.18)',
        border: '1px solid rgba(167,139,250,0.3)',
        color: '#A78BFA',
        borderRadius: 8,
        padding: '3px 12px',
        fontSize: 13,
        fontWeight: 700,
        fontFamily: 'monospace',
        letterSpacing: 2,
    },

    /* How it works */
    howSection: {
        marginBottom: 24,
    },
    stepList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 18,
        padding: '4px 0',
        overflow: 'hidden',
    },
    stepItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
    },
    stepIconWrap: {
        width: 38,
        height: 38,
        borderRadius: 10,
        background: 'rgba(124,58,237,0.15)',
        border: '1px solid rgba(167,139,250,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    stepText: {
        flex: 1,
    },
    stepLabel: {
        fontSize: 14,
        fontWeight: 600,
        color: 'rgba(255,255,255,0.88)',
        margin: '0 0 2px',
    },
    stepSub: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.4)',
        margin: 0,
    },

    /* People section */
    peopleSection: {
        marginBottom: 8,
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        padding: '32px 0',
    },
    spinner: {
        width: 28,
        height: 28,
        border: '3px solid rgba(255,255,255,0.1)',
        borderTopColor: '#19bfb1',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    emptyState: {
        textAlign: 'center',
        padding: '36px 16px',
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 18,
    },
    emptyTitle: {
        fontSize: 15,
        fontWeight: 600,
        color: 'rgba(255,255,255,0.5)',
        margin: '0 0 6px',
    },
    emptySub: {
        fontSize: 13,
        color: 'rgba(255,255,255,0.3)',
        margin: 0,
    },
    personList: {
        display: 'flex',
        flexDirection: 'column',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 18,
        overflow: 'hidden',
    },
    personItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
    },
    personAvatar: {
        width: 42,
        height: 42,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 17,
        fontWeight: 700,
        color: '#fff',
        flexShrink: 0,
    },
    personInfo: {
        flex: 1,
    },
    personName: {
        fontSize: 14,
        fontWeight: 600,
        color: 'rgba(255,255,255,0.88)',
        margin: '0 0 2px',
    },
    personDate: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.4)',
        margin: 0,
    },
    personBonus: {
        textAlign: 'right',
    },
    personBonusAmt: {
        fontSize: 15,
        fontWeight: 700,
        color: '#10B981',
        margin: '0 0 1px',
    },
    personBonusPending: {
        fontSize: 15,
        fontWeight: 700,
        color: 'rgba(255,255,255,0.35)',
        margin: '0 0 1px',
    },
    personBonusLabel: {
        fontSize: 10,
        color: 'rgba(255,255,255,0.35)',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        margin: 0,
    },
};

export default Referral;
